import { AfterViewInit, Component, ElementRef, HostListener, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { MotorService } from '../services/motor.service';

interface MotorReading {
  id?: number;
  Deviceid: string;
  reading_time: string;
  inserted_at?: string;
  Vibration_Sensor_1?: number;
  Vibration_Sensor_2?: number;
  Temperature_Sensor_1?: number;
  Temperature_Sensor_2?: number;
  Voltage_R?: number;
  Voltage_Y?: number;
  Voltage_B?: number;
  Current_R?: number;
  Current_Y?: number;
  Current_B?: number;
  Frequency?: number;
  Active_Power?: number;
  Power_Factor?: number;
  Voltage_THD_R?: number;
  Voltage_THD_Y?: number;
  Voltage_THD_B?: number;
  Current_THD_R?: number;
  Current_THD_Y?: number;
  Current_THD_B?: number;
  Version?: number;
  Network?: number;
  [key: string]: any;
}

interface ChartSeries { name: string; color: string; line: string; area: string; dots: { x: number; y: number }[]; }
interface ChartModel {
  plot: { l: number; r: number; t: number; b: number };
  series: ChartSeries[];
  yTicks: { y: number; label: string }[];
  xTicks: { x: number; label: string; anchor: string }[];
}
interface HoverInfo {
  x: number; xPct: number; time: string; flip: boolean;
  items: { name: string; color: string; value: string; y: number }[];
}
interface AnalyticsRow { label: string; unit: string; min: string; max: string; avg: string; }

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

@Component({
  selector: 'app-motor-dashboard',
  templateUrl: './motor-dashboard.component.html',
  styleUrls: ['./motor-dashboard.component.scss']
})
export class MotorDashboardComponent implements OnInit, AfterViewInit, OnDestroy {
  @ViewChild('overlayHost', { static: false }) overlayHost: ElementRef;

  devices: any[] = [];
  readings: MotorReading[] = [];
  graphReadings: MotorReading[] = [];
  reportReadings: MotorReading[] = [];
  selectedDevice = 'M001J26';
  detailReading: MotorReading = null;
  loading = false;
  reportLoading = false;
  apiOnline = true;
  errorMessage = '';
  showDetails = false;
  showHistory = false;
  lastUpdated: Date = new Date();

  selectedTrend = 'temperature';
  graphRange = 'today';
  graphFrom = '';
  graphTo = '';
  reportRange = 'today';
  reportFrom = '';
  reportTo = '';

  chart: ChartModel = null;
  hover: HoverInfo = null;
  analytics: AnalyticsRow[] = [];

  // SVG chart geometry (viewBox units)
  readonly W = 760;
  readonly H = 420;
  private readonly PAD = { l: 54, r: 20, t: 14, b: 38 };
  private chartXs: number[] = [];
  private chartScale = { min: 0, max: 1 };


  readonly graphRanges = [
    { value: 'today', label: 'Today' }, { value: 'yesterday', label: 'Yesterday' },
    { value: 'week', label: '7 days' }, { value: 'month', label: '30 days' },
    { value: 'custom', label: 'Custom' }
  ];
  readonly reportRanges = this.graphRanges;
  readonly trendOptions = [
    { value: 'temperature', label: 'Temperature', unit: '°C', keys: ['Temperature_Sensor_1', 'Temperature_Sensor_2'], names: ['Temperature 1', 'Temperature 2'], colors: ['#e5484d', '#f59f00'] },
    {
      value: 'vibration', label: 'Vibration', unit: 'mm/s',
      keys: ['Vibration_Velocity_X_1', 'Vibration_Velocity_y_1', 'Vibration_Velocity_Z_1', 'Vibration_Velocity_X_2', 'Vibration_Velocity_y_2', 'Vibration_Velocity_Z_2'],
      names: ['S1 X', 'S1 Y', 'S1 Z', 'S2 X', 'S2 Y', 'S2 Z'],
      colors: ['#2563eb', '#0ea5e9', '#06b6d4', '#7c3aed', '#a855f7', '#d946ef']
    },
    { value: 'current', label: 'Current', unit: 'A', keys: ['Current_R', 'Current_Y', 'Current_B'], names: ['R phase', 'Y phase', 'B phase'], colors: ['#e5484d', '#f59f00', '#2563eb'] },
    { value: 'voltage', label: 'Voltage', unit: 'V', keys: ['Voltage_R', 'Voltage_Y', 'Voltage_B'], names: ['R phase', 'Y phase', 'B phase'], colors: ['#e5484d', '#f59f00', '#2563eb'] }
  ];
  private readonly analyticsDefs = [
    { label: 'Vib. 1 X', key: 'Vibration_Velocity_X_1', unit: '' },
    { label: 'Vib. 1 Y', key: 'Vibration_Velocity_y_1', unit: '' },
    { label: 'Vib. 1 Z', key: 'Vibration_Velocity_Z_1', unit: '' },
    { label: 'Vib. 2 X', key: 'Vibration_Velocity_X_2', unit: '' },
    { label: 'Vib. 2 Y', key: 'Vibration_Velocity_y_2', unit: '' },
    { label: 'Vib. 2 Z', key: 'Vibration_Velocity_Z_2', unit: '' },
    { label: 'Temperature 1', key: 'Temperature_Sensor_1', unit: '°C' },
    { label: 'Temperature 2', key: 'Temperature_Sensor_2', unit: '°C' },
    { label: 'Active power', key: 'Active_Power', unit: '' },
    { label: 'Power factor', key: 'Power_Factor', unit: '' }
  ];

  alerts: any[] = [];
  thresholds: any = {};
  showThresholds = false;
 readonly thresholdGroups = [
  { title: 'Temperature', items: [
    { key: 'Temperature_Sensor_1', label: 'Temperature 1', unit: '°C' },
    { key: 'Temperature_Sensor_2', label: 'Temperature 2', unit: '°C' } ] },
  { title: 'Vibration', items: [
    { key: 'Vibration_Velocity_X_1', label: 'Vibration 1 X', unit: 'mm/s' },
    { key: 'Vibration_Velocity_y_1', label: 'Vibration 1 Y', unit: 'mm/s' },
    { key: 'Vibration_Velocity_Z_1', label: 'Vibration 1 Z', unit: 'mm/s' },
    { key: 'Vibration_Velocity_X_2', label: 'Vibration 2 X', unit: 'mm/s' },
    { key: 'Vibration_Velocity_y_2', label: 'Vibration 2 Y', unit: 'mm/s' },
    { key: 'Vibration_Velocity_Z_2', label: 'Vibration 2 Z', unit: 'mm/s' } ] },
  { title: 'Current', items: [
    { key: 'Current_R', label: 'Current R', unit: 'A' },
    { key: 'Current_Y', label: 'Current Y', unit: 'A' },
    { key: 'Current_B', label: 'Current B', unit: 'A' } ] },
  { title: 'Voltage', items: [
    { key: 'Voltage_R', label: 'Voltage R', unit: 'V' },
    { key: 'Voltage_Y', label: 'Voltage Y', unit: 'V' },
    { key: 'Voltage_B', label: 'Voltage B', unit: 'V' } ] },
  { title: 'Power', items: [
    { key: 'Frequency', label: 'Frequency', unit: 'Hz' },
    { key: 'Power_Factor', label: 'Power factor', unit: '' },
    { key: 'Active_Power', label: 'Active power', unit: '' } ] }
];
  readonly thresholdParams: any[] = this.thresholdGroups.reduce((a: any[], g) => a.concat(g.items), []);

  private timer: any;

  paramLabel(key: string): string {
    const p = this.thresholdParams.find(x => x.key === key);
    return p ? p.label + (p.unit ? ' (' + p.unit + ')' : '') : key;
  }

  /** One separate object per parameter. This is what fixes the "all toggle together" bug. */
  private blankThresholds(): any {
    const m: any = {};
    this.thresholdParams.forEach(p => m[p.key] = { warn_low: null, warn_high: null, crit_low: null, crit_high: null, enabled: 1 });
    return m;
  }

  closeThresholds(): void { this.showThresholds = false; this.loadThresholds(); }   // discards unsaved edits
  get openAlerts(): any[] { return this.alerts.filter(a => a.status === 'open'); }
  constructor(private motorService: MotorService) { }

  /* ------------------------------ lifecycle ------------------------------ */

  ngOnInit(): void {
    this.thresholds = this.blankThresholds();
    this.loadDevices();
    this.loadReadings();
    this.setGraphRange('today');
    this.loadThresholds();
    this.loadAlerts();
  }

  ngAfterViewInit(): void {
    // Move the modal host to <body> so ancestor transforms/overflow/z-index of the app shell can't shift it.
    if (this.overlayHost) { document.body.appendChild(this.overlayHost.nativeElement); }
  }

  ngOnDestroy(): void {
    clearInterval(this.timer);
    const el = this.overlayHost && this.overlayHost.nativeElement;
    if (el && el.parentNode) { el.parentNode.removeChild(el); }
  }

  @HostListener('document:keydown.escape')
  onEscape(): void { this.closeDetails(); this.closeHistory(); this.closeThresholds(); }

  /* ------------------------------- getters ------------------------------- */

  get latest(): MotorReading[] {
    const seen: { [key: string]: boolean } = {};
    const result: MotorReading[] = [];
    this.readings.forEach(r => {
      if (r && r.Deviceid && !seen[r.Deviceid]) { seen[r.Deviceid] = true; result.push(r); }
    });
    return result;
  }
  // readings are sorted newest-first, so the first one is always the latest reading
  get latestReading(): MotorReading { return this.readings[0] || null; }
  get averageTemperature(): number { return this.averageLatest('Temperature_Sensor_1'); }
  get averageVibration(): number { return this.averageLatest('Vibration_Velocity_X_1'); }
  get activeTrend(): any { return this.trendOptions.find(x => x.value === this.selectedTrend) || this.trendOptions[0]; }
  get selectedDeviceLabel(): string { return this.selectedDevice === 'all' ? 'All motors' : this.selectedDevice; }
  get activeRangeLabel(): string {
    const r = this.graphRanges.find(x => x.value === this.graphRange);
    return r ? r.label : '';
  }

  deviceName(d: any): string { return d.Deviceid || d.deviceId || d.device_id; }

  /* ------------------------------ data loading ---------------------------- */

  reloadAll(): void {
    this.loadDevices();
    this.loadReadings();
    if (this.graphRange === 'custom') { this.loadGraphReadings(); } else { this.setGraphRange(this.graphRange); }
    if (this.showHistory) { this.loadReportReadings(); }
  }

  loadDevices(): void {
    this.motorService.getDevices().subscribe((response: any) => {
      const rows = Array.isArray(response) ? response : (response.data || response.devices || response.rows || []);
      this.devices = rows.map((item: any) => typeof item === 'string' ? { Deviceid: item } : item);
    }, error => console.error('Motor devices API failed:', error));
  }

  loadReadings(): void {
    this.loading = true;
    const filters: any = { limit: 1000 };
    if (this.selectedDevice !== 'all') { filters.deviceId = this.selectedDevice; }
    this.motorService.getReadings(filters).subscribe((response: any) => {
      const rows = Array.isArray(response) ? response : (response.data || response.readings || response.rows || []);
      this.readings = this.sortDesc(rows);
      this.lastUpdated = new Date();
      this.apiOnline = true;
      this.errorMessage = '';
      this.loading = false;
    }, error => {
      this.loading = false;
      this.apiOnline = false;
      this.errorMessage = 'Could not load motor readings. Check that the API is running, then press refresh.';
      console.error('Motor readings API failed:', error);
    });
  }

  selectDevice(deviceId: string): void {
    this.selectedDevice = deviceId;
    this.loadReadings();
    this.loadGraphReadings();
    this.loadThresholds();
    this.loadAlerts();
    if (this.showHistory) { this.loadReportReadings(); }
  }

  changeTrend(value: string): void { this.selectedTrend = value; this.buildChart(); }

  setGraphRange(range: string): void {
    this.graphRange = range;
    if (range === 'custom') { return; }
    const dates = this.rangeDates(range);
    this.graphFrom = this.toLocalInput(dates.from);
    this.graphTo = this.toLocalInput(dates.to);
    this.loadGraphReadings();
  }

  applyGraphRange(): void {
    if (!this.graphFrom || !this.graphTo || new Date(this.graphFrom) > new Date(this.graphTo)) {
      this.errorMessage = 'Choose a valid graph start and end date.'; return;
    }
    this.errorMessage = '';
    this.loadGraphReadings();
  }

  private loadGraphReadings(): void {
    const filters: any = { limit: 5000 };
    if (this.selectedDevice !== 'all') { filters.deviceId = this.selectedDevice; }
    if (this.graphFrom) { filters.from = this.graphFrom.replace('T', ' ') + ':00'; }
    if (this.graphTo) { filters.to = this.graphTo.replace('T', ' ') + ':59'; }
    this.motorService.getReadings(filters).subscribe((response: any) => {
      const rows = Array.isArray(response) ? response : (response.data || response.readings || response.rows || []);
      this.graphReadings = rows.filter((r: MotorReading) => r && r.reading_time)
        .sort((a: MotorReading, b: MotorReading) => this.ts(a.reading_time) - this.ts(b.reading_time));
      this.buildChart();
      this.buildAnalytics();
    }, error => {
      console.error('Motor graph API failed:', error);
      this.graphReadings = []; this.chart = null; this.buildAnalytics();
    });
  }

  /* ------------------------------ modals ---------------------------------- */

  openDetails(reading: MotorReading): void { this.detailReading = reading; this.showDetails = true; }
  closeDetails(): void { this.showDetails = false; }

  openHistory(): void {
    this.showHistory = true;
    if (!this.reportFrom || !this.reportTo) { this.setReportRange('today'); }
    else { this.loadReportReadings(); }
  }
  closeHistory(): void { this.showHistory = false; }

  setReportRange(range: string): void {
    this.reportRange = range;
    if (range === 'custom') { return; }
    const dates = this.rangeDates(range);
    this.reportFrom = this.toLocalInput(dates.from);
    this.reportTo = this.toLocalInput(dates.to);
    this.loadReportReadings();
  }

  applyReportRange(): void {
    if (!this.reportFrom || !this.reportTo || new Date(this.reportFrom) > new Date(this.reportTo)) {
      this.errorMessage = 'Choose a valid report start and end date.'; return;
    }
    this.errorMessage = '';
    this.loadReportReadings();
  }

  private loadReportReadings(): void {
    this.reportLoading = true;
    const filters: any = { limit: 5000 };
    if (this.selectedDevice !== 'all') { filters.deviceId = this.selectedDevice; }
    if (this.reportFrom) { filters.from = this.reportFrom.replace('T', ' ') + ':00'; }
    if (this.reportTo) { filters.to = this.reportTo.replace('T', ' ') + ':59'; }
    this.motorService.getReadings(filters).subscribe((response: any) => {
      const rows = Array.isArray(response) ? response : (response.data || response.readings || response.rows || []);
      this.reportReadings = this.sortDesc(rows);
      this.reportLoading = false;
    }, error => { this.reportLoading = false; console.error('Motor report API failed:', error); });
  }

  /* ------------------------------ formatting ------------------------------ */

  value(reading: MotorReading, key: string): number {
    const raw = reading && reading[key]; const n = Number(raw);
    return raw !== null && raw !== undefined && raw !== '' && isFinite(n) ? n : null as any;
  }

  format(value: any, decimals: number = 2): string {
    const n = Number(value);
    return value === null || value === undefined || value === '' || !isFinite(n) ? '—' : n.toFixed(decimals);
  }

  /**
   * reading_time is a wall-clock time sent by the device (MySQL DATETIME, no timezone).
   * We must show it exactly as stored. If the API serialises it as an ISO string
   * ("2026-10-13T14:38:10.000Z") a normal `new Date()` / date pipe would shift it by the
   * browser's UTC offset (+05:30 in India). So we read the numbers out of the string and
   * ignore any "Z" / offset suffix.
   */
  private parseReadingDate(value: any): Date {
    if (value instanceof Date) { return value; }
    if (typeof value === 'string') {
      const m = value.match(/^(\d{4})-(\d{2})-(\d{2})[ T](\d{2}):(\d{2})(?::(\d{2}))?/);
      if (m) { return new Date(+m[1], +m[2] - 1, +m[3], +m[4], +m[5], +(m[6] || 0)); }
    }
    return new Date(value);
  }
  private ts(value: any): number { return this.parseReadingDate(value).getTime(); }
  private sortDesc(rows: MotorReading[]): MotorReading[] {
    return rows.slice().sort((a, b) => this.ts(b.reading_time) - this.ts(a.reading_time));
  }
  private p2(n: number): string { return n < 10 ? '0' + n : String(n); }

  /** "13 Oct 2026, 14:38:10" — same value that is stored in the database. */
  dateLabel(value: any, withSeconds: boolean = true): string {
    if (!value) { return '—'; }
    const d = this.parseReadingDate(typeof value === 'string' ? value.replace('T', ' ') : value);
    if (isNaN(d.getTime())) { return String(value); }
    return this.p2(d.getDate()) + ' ' + MONTHS[d.getMonth()] + ' ' + d.getFullYear() + ', ' +
      this.p2(d.getHours()) + ':' + this.p2(d.getMinutes()) + (withSeconds ? ':' + this.p2(d.getSeconds()) : '');
  }

  timeAgo(value: any): string {
    const diff = Date.now() - this.ts(value);
    if (isNaN(diff)) { return ''; }
    const min = Math.floor(diff / 60000);
    if (min < 1) { return 'just now'; }
    if (min < 60) { return min + ' min ago'; }
    const h = Math.floor(min / 60);
    if (h < 24) { return h + ' h ago'; }
    return Math.floor(h / 24) + ' d ago';
  }

  isOnline(reading: MotorReading): boolean {
    const age = Date.now() - this.ts(reading.reading_time);
    return !isNaN(age) && age <= 30 * 60 * 1000;
  }

  /* ------------------------------- chart ---------------------------------- */

  private buildChart(): void {
    this.hover = null;
    const rows = this.graphReadings; const trend = this.activeTrend;
    const keys = trend.keys as string[];
    let min = Infinity; let max = -Infinity;
    rows.forEach(r => keys.forEach(k => {
      const v = this.value(r, k);
      if (v !== null) { if (v < min) { min = v; } if (v > max) { max = v; } }
    }));
    if (!isFinite(min)) { this.chart = null; this.chartXs = []; return; }

    const pad = max === min ? Math.max(Math.abs(max) * 0.05, 1) : (max - min) * 0.12;
    min -= pad; max += pad;
    this.chartScale = { min, max };

    const P = this.PAD;
    const plot = { l: P.l, r: this.W - P.r, t: P.t, b: this.H - P.b };
    const times = rows.map(r => this.ts(r.reading_time));
    const t0 = times[0]; const t1 = times[times.length - 1];
    const xOf = (t: number) => t1 === t0 ? (plot.l + plot.r) / 2 : plot.l + (t - t0) / (t1 - t0) * (plot.r - plot.l);
    const yOf = (v: number) => plot.t + (1 - (v - min) / (max - min)) * (plot.b - plot.t);
    this.chartXs = times.map(xOf);

    const showDots = rows.length <= 60;
    const series: ChartSeries[] = keys.map((key, i) => {
      const pts: { x: number; y: number }[] = [];
      rows.forEach((r, idx) => { const v = this.value(r, key); if (v !== null) { pts.push({ x: this.chartXs[idx], y: yOf(v) }); } });
      const line = this.smoothPath(pts);
      const area = pts.length > 1 ? line + 'L' + pts[pts.length - 1].x.toFixed(1) + ',' + plot.b + 'L' + pts[0].x.toFixed(1) + ',' + plot.b + 'Z' : '';
      return { name: trend.names[i], color: trend.colors[i], line, area, dots: showDots ? pts : [] };
    });

    const step = (max - min) / 4;
    const dec = step >= 10 ? 0 : step >= 1 ? 1 : 2;
    const yTicks = [0, 1, 2, 3, 4].map(i => { const v = min + step * i; return { y: yOf(v), label: v.toFixed(dec) }; });

    const span = t1 - t0;
    const xTicks: { x: number; label: string; anchor: string }[] = [];
    if (span === 0) {
      xTicks.push({ x: xOf(t0), label: this.dateLabel(rows[0].reading_time, false), anchor: 'middle' });
    } else {
      for (let i = 0; i < 5; i++) {
        const t = t0 + span * i / 4;
        const d = new Date(t);
        const label = span > 36 * 3600000
          ? d.getDate() + ' ' + MONTHS[d.getMonth()]
          : (span > 24 * 3600000 ? d.getDate() + ' ' + MONTHS[d.getMonth()] + ' ' : '') + this.p2(d.getHours()) + ':' + this.p2(d.getMinutes());
        xTicks.push({ x: xOf(t), label, anchor: i === 0 ? 'start' : (i === 4 ? 'end' : 'middle') });
      }
    }
    this.chart = { plot, series, yTicks, xTicks };
  }

  /** Monotone cubic interpolation: smooth curves that never overshoot the real data. */
  private smoothPath(p: { x: number; y: number }[]): string {
    const n = p.length; const f = (v: number) => v.toFixed(1);
    if (n === 0) { return ''; }
    if (n === 1) { return 'M' + f(p[0].x) + ',' + f(p[0].y); }
    if (n === 2) { return 'M' + f(p[0].x) + ',' + f(p[0].y) + 'L' + f(p[1].x) + ',' + f(p[1].y); }
    const dx: number[] = []; const m: number[] = []; const t: number[] = [];
    for (let i = 0; i < n - 1; i++) { dx[i] = p[i + 1].x - p[i].x; m[i] = dx[i] === 0 ? 0 : (p[i + 1].y - p[i].y) / dx[i]; }
    t[0] = m[0]; t[n - 1] = m[n - 2];
    for (let i = 1; i < n - 1; i++) { t[i] = m[i - 1] * m[i] <= 0 ? 0 : (m[i - 1] + m[i]) / 2; }
    for (let i = 0; i < n - 1; i++) {
      if (m[i] === 0) { t[i] = 0; t[i + 1] = 0; continue; }
      const a = t[i] / m[i]; const b = t[i + 1] / m[i]; const s = a * a + b * b;
      if (s > 9) { const k = 3 / Math.sqrt(s); t[i] = k * a * m[i]; t[i + 1] = k * b * m[i]; }
    }
    let d = 'M' + f(p[0].x) + ',' + f(p[0].y);
    for (let i = 0; i < n - 1; i++) {
      const h = dx[i] / 3;
      d += 'C' + f(p[i].x + h) + ',' + f(p[i].y + t[i] * h) + ' ' + f(p[i + 1].x - h) + ',' + f(p[i + 1].y - t[i + 1] * h) + ' ' + f(p[i + 1].x) + ',' + f(p[i + 1].y);
    }
    return d;
  }

  onChartMove(ev: MouseEvent, svg: SVGSVGElement): void {
    if (!this.chart || !this.chartXs.length) { return; }
    const rect = svg.getBoundingClientRect();
    const px = (ev.clientX - rect.left) / rect.width * this.W;
    let best = 0; let bestDist = Infinity;
    this.chartXs.forEach((x, i) => { const d = Math.abs(x - px); if (d < bestDist) { bestDist = d; best = i; } });
    const r = this.graphReadings[best]; const trend = this.activeTrend;
    const plot = this.chart.plot; const sc = this.chartScale;
    const items: { name: string; color: string; value: string; y: number }[] = [];
    (trend.keys as string[]).forEach((k, i) => {
      const v = this.value(r, k);
      if (v !== null) {
        items.push({
          name: trend.names[i], color: trend.colors[i], value: v.toFixed(2) + ' ' + trend.unit,
          y: plot.t + (1 - (v - sc.min) / (sc.max - sc.min)) * (plot.b - plot.t)
        });
      }
    });
    const x = this.chartXs[best];
    this.hover = { x, xPct: x / this.W * 100, time: this.dateLabel(r.reading_time), flip: x / this.W > 0.6, items };
  }

  /* ------------------------------ analytics ------------------------------- */

  private buildAnalytics(): void {
    this.analytics = this.analyticsDefs.map(def => {
      const vals = this.graphReadings.map(r => this.value(r, def.key)).filter(v => v !== null) as number[];
      if (!vals.length) { return { label: def.label, unit: def.unit, min: '—', max: '—', avg: '—' }; }
      const min = Math.min.apply(null, vals); const max = Math.max.apply(null, vals);
      const avg = vals.reduce((a, b) => a + b, 0) / vals.length;
      return { label: def.label, unit: def.unit, min: this.format(min), max: this.format(max), avg: this.format(avg) };
    });
  }

  /* ------------------------------- export --------------------------------- */

  exportHistoryCsv(): void {
    const columns = [
      ['Device ID', 'Deviceid'], ['Reading Time', 'reading_time'],
      ['Vibration 1 X', 'Vibration_Velocity_X_1'], ['Vibration 1 Y', 'Vibration_Velocity_y_1'], ['Vibration 1 Z', 'Vibration_Velocity_Z_1'],
      ['Vibration 2 X', 'Vibration_Velocity_X_2'], ['Vibration 2 Y', 'Vibration_Velocity_y_2'], ['Vibration 2 Z', 'Vibration_Velocity_Z_2'], ['Temperature Sensor 1', 'Temperature_Sensor_1'], ['Temperature Sensor 2', 'Temperature_Sensor_2'],
      ['Voltage R', 'Voltage_R'], ['Voltage Y', 'Voltage_Y'], ['Voltage B', 'Voltage_B'],
      ['Current R', 'Current_R'], ['Current Y', 'Current_Y'], ['Current B', 'Current_B'],
      ['Frequency', 'Frequency'], ['Active Power', 'Active_Power'], ['Power Factor', 'Power_Factor'],
      ['Voltage THD R', 'Voltage_THD_R'], ['Voltage THD Y', 'Voltage_THD_Y'], ['Voltage THD B', 'Voltage_THD_B'],
      ['Current THD R', 'Current_THD_R'], ['Current THD Y', 'Current_THD_Y'], ['Current THD B', 'Current_THD_B'],
      ['Version', 'Version'], ['Network', 'Network']
    ];
    const esc = (value: any) => '"' + String(value === null || value === undefined ? '' : value).replace(/"/g, '""') + '"';
    const cell = (r: MotorReading, key: string) => key === 'reading_time' ? String(r[key]).replace('T', ' ').slice(0, 19) : r[key];
    const lines = [columns.map(c => esc(c[0])).join(',')];
    this.reportReadings.forEach(r => lines.push(columns.map(c => esc(cell(r, c[1]))).join(',')));
    const blob = new Blob(['\ufeff' + lines.join('\r\n')], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob); const a = document.createElement('a');
    a.href = url;
    a.download = 'motor-readings-' + (this.selectedDevice === 'all' ? 'all' : this.selectedDevice) + '-' + this.toLocalInput(new Date()).slice(0, 10) + '.csv';
    a.click(); URL.revokeObjectURL(url);
  }

  /* ------------------------------- helpers -------------------------------- */

  private averageLatest(key: string): number {
    const rows = this.latest.filter(r => this.value(r, key) !== null);
    return rows.length ? rows.reduce((sum, r) => sum + Number(r[key]), 0) / rows.length : null as any;
  }

  private rangeDates(range: string): { from: Date; to: Date } {
    const now = new Date(); const from = new Date(now); const to = new Date(now);
    if (range === 'today') { from.setHours(0, 0, 0, 0); to.setHours(23, 59, 59, 0); }
    else if (range === 'yesterday') { from.setDate(from.getDate() - 1); from.setHours(0, 0, 0, 0); to.setTime(from.getTime()); to.setHours(23, 59, 59, 0); }
    else if (range === 'week') { from.setDate(from.getDate() - 6); from.setHours(0, 0, 0, 0); to.setHours(23, 59, 59, 0); }
    else if (range === 'month') { from.setDate(from.getDate() - 29); from.setHours(0, 0, 0, 0); to.setHours(23, 59, 59, 0); }
    return { from, to };
  }

  private toLocalInput(date: Date): string {
    return date.getFullYear() + '-' + this.p2(date.getMonth() + 1) + '-' + this.p2(date.getDate()) + 'T' + this.p2(date.getHours()) + ':' + this.p2(date.getMinutes());
  }
  private thresholdDevice(): string { return this.selectedDevice === 'all' ? '*' : this.selectedDevice; }

  loadThresholds(): void {
    const dev = this.thresholdDevice();
    this.motorService.getThresholds(dev).subscribe((res: any) => {
      const rows = res.data || []; const m = this.blankThresholds();
      rows.filter((r: any) => r.Deviceid === '*').forEach((r: any) => { if (m[r.parameter]) { m[r.parameter] = { ...m[r.parameter], ...r }; } });
      rows.filter((r: any) => r.Deviceid === dev).forEach((r: any) => { if (m[r.parameter]) { m[r.parameter] = { ...m[r.parameter], ...r }; } });
      this.thresholds = m;
    }, e => console.error('Thresholds API failed:', e));
  }

  loadAlerts(): void {
    const f: any = { limit: 200 };
    if (this.selectedDevice !== 'all') { f.deviceId = this.selectedDevice; }
    this.motorService.getAlerts(f).subscribe((res: any) => { this.alerts = res.data || []; },
      e => console.error('Alerts API failed:', e));
  }

  ackAlert(a: any): void { this.motorService.ackAlert(a.id).subscribe(() => this.loadAlerts()); }

  openThresholds(): void { this.showThresholds = true; }

  saveThresholds(): void {
    const items = this.thresholdParams.map(p => ({ parameter: p.key, ...this.thresholds[p.key] }));   // send all rows, so cleared boxes are saved too
    this.motorService.saveThresholds({ Deviceid: this.thresholdDevice(), items }).subscribe(() => {
      this.showThresholds = false; this.loadThresholds(); this.loadAlerts();
    }, () => this.errorMessage = 'Could not save thresholds.');
  }

  /** CSS class for a value: red if critical, amber if warning */
  level(reading: MotorReading, key: string): string {
    const t = this.thresholds[key]; const v = this.value(reading, key);
    if (!t || v === null || t.enabled === 0) { return ''; }
    if ((t.crit_high != null && v >= t.crit_high) || (t.crit_low != null && v <= t.crit_low)) { return 'mm-crit'; }
    if ((t.warn_high != null && v >= t.warn_high) || (t.warn_low != null && v <= t.warn_low)) { return 'mm-warn'; }
    return '';
  }

  exportAlertsCsv(): void {
    const esc = (v: any) => '"' + String(v == null ? '' : v).replace(/"/g, '""') + '"';
    const cols = ['Deviceid', 'reading_time', 'parameter', 'value', 'direction', 'threshold', 'severity', 'status'];
    const lines = [cols.join(',')].concat(this.alerts.map(a => cols.map(c => esc(a[c])).join(',')));
    const url = URL.createObjectURL(new Blob(['\ufeff' + lines.join('\r\n')], { type: 'text/csv;charset=utf-8;' }));
    const el = document.createElement('a'); el.href = url; el.download = 'motor-alerts.csv'; el.click(); URL.revokeObjectURL(url);
  }
}